/**
 * U2000PasswordProgressionPopup
 *
 * ATTENTION :
 * Pour que le positionnement du popup fonctionne correctement (juste à gauche ou à droite du champ),
 * le parent direct du champ et du popup doit avoir la classe Tailwind `relative`.
 *
 * Exemple d'intégration :
 *
 * <div className="relative flex">
 *   <input ... />
 *   <U2000PasswordProgressionPopup position="RIGHT_CENTER" ... />
 * </div>
 *
 * Utilisez :
 *   - position="RIGHT_CENTER" (left-full ml-4) pour afficher à droite du champ
 *   - position="LEFT_CENTER" (right-full mr-4) pour afficher à gauche du champ
 */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

type PopupPosition =
  | "LEFT_TOP"
  | "LEFT_CENTER"
  | "LEFT_BOTTOM"
  | "RIGHT_TOP"
  | "RIGHT_CENTER"
  | "RIGHT_BOTTOM"
  | "TOP_LEFT"
  | "TOP_CENTER"
  | "TOP_RIGHT"
  | "BOTTOM_LEFT"
  | "BOTTOM_CENTER"
  | "BOTTOM_RIGHT";

interface U2000PasswordProgressionPopupProps {
  password?: string;
  show?: boolean;
  position?: PopupPosition;
}

interface PasswordCriterion {
  label: string;
  test: (pw: string) => boolean;
}

function getPositionClasses(position: PopupPosition = "RIGHT_CENTER"): string {
  // Par défaut : LEFT_TOP
  switch (position) {
    case "LEFT_TOP":
      return "right-full top-0 mr-3";
    case "LEFT_CENTER":
      return "right-full top-1/2 -translate-y-1/2 mr-3";
    case "LEFT_BOTTOM":
      return "right-full bottom-0 mr-3";
    case "RIGHT_TOP":
      return "left-full top-0 ml-3";
    case "RIGHT_CENTER":
      return "left-full top-1/2 -translate-y-1/2 ml-3";
    case "RIGHT_BOTTOM":
      return "left-full bottom-0 ml-3";
    case "TOP_LEFT":
      return "bottom-full left-0 mb-3";
    case "TOP_CENTER":
      return "bottom-full left-1/2 -translate-x-1/2 mb-3";
    case "TOP_RIGHT":
      return "bottom-full right-0 mb-3";
    case "BOTTOM_LEFT":
      return "top-full left-0 mt-3";
    case "BOTTOM_CENTER":
      return "top-full left-1/2 -translate-x-1/2 mt-3";
    case "BOTTOM_RIGHT":
      return "top-full right-0 mt-3";
    default:
      return "left-0 top-0";
  }
}

const U2000PasswordProgressionPopup: React.FC<U2000PasswordProgressionPopupProps> = ({ password = "", show = false, position = "LEFT_TOP" }) => {
  const { t } = useTranslation();
  const passwordCriteria: PasswordCriterion[] = [
    {
      label: t("msgPasswordMin8Chars"),
      test: (pw: string) => pw.length >= 8,
    },
    {
      label: t("msgPasswordUppercase"),
      test: (pw: string) => /[A-Z]/.test(pw),
    },
    {
      label: t("msgPasswordDigit"),
      test: (pw: string) => /[0-9]/.test(pw),
    },
    {
      label: t("msgPasswordSymbol"),
      test: (pw: string) => /[!@#$%&*_?|]/.test(pw),
    },
  ];

  const passwordProgress: boolean[] = passwordCriteria.map((c) => c.test(password));

  useEffect(() => {
    // Peut être utilisé pour des effets secondaires si besoin
    // const passwordProgress = passwordCriteria.map((c) => c.test(password));
    // const allValid = passwordProgress.every(Boolean);
  }, [password]);

  if (!show) return null;
  return (
    <div
      className={`absolute z-20 w-[400px] rounded-lg border border-gray-300 bg-white p-5 shadow-xl
                   text-sm animate-fade-in ${getPositionClasses(position)}`}
    >
      <div className="mb-4 font-semibold text-gray-900 text-base">{t("lblPasswordCriteria")}</div>
      <ul className="space-y-3">
        {passwordCriteria.map((c, idx) => (
          <li
            key={idx}
            className={`flex items-center transition-all duration-300 ease-in-out
                      ${passwordProgress[idx] ? "text-green-700 font-medium" : "text-gray-600 font-normal"}`}
          >
            <span className="mr-3 flex items-center transition-transform duration-200 ease-in-out">
              {passwordProgress[idx] ? (
                <div className="flex items-center justify-center w-5 h-5 bg-green-100 rounded-full">
                  <svg className="h-3 w-3 text-green-600 transition-all duration-200 ease-in-out" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="flex items-center justify-center w-5 h-5 bg-gray-100 rounded-full">
                  <svg className="h-3 w-3 text-gray-400 transition-all duration-200 ease-in-out" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
            </span>
            <span className="text-sm leading-relaxed">{c.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default U2000PasswordProgressionPopup;
